import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";

import { InfinityHearts } from "../components/InfinityHearts";
import { useT } from "../i18n";
import { tierOf, type ProfileTier } from "./chromePlan";
import { pairAlignmentScore } from "./engine";
import { scoreBand } from "./weights";
import type { MatchProfile } from "./types";

type Phase = "dark" | "stack" | "deal" | "landed" | "exit";

type Seat = { left: number; top: number; width: number; height: number };

/** InfinityHearts play ~ draw + breath + split + fly. */
const SIGN_MS = 2400;
const SIGN_REDUCED_MS = 320;
/** One card flies, then the next — like the score load beat. */
const DEAL_STAGGER_MS = 520;
const SCORE_MS = 480;

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function measureSeats(stage: HTMLElement | null): Seat[] {
  if (!stage) return [];
  const wraps = stage.querySelectorAll<HTMLElement>(".match-week-tile.match-week-tile--fifa");
  if (!wraps.length) return [];
  const stageRect = stage.getBoundingClientRect();
  return Array.from(wraps).map((el) => {
    const r = el.getBoundingClientRect();
    return {
      left: r.left - stageRect.left,
      top: r.top - stageRect.top,
      width: r.width,
      height: r.height,
    };
  });
}

function stackSeats(stage: HTMLElement | null, n: number): Seat[] {
  const w = stage?.clientWidth || 320;
  const h = stage?.clientHeight || 420;
  const cardW = Math.min(152, w * 0.44);
  const cardH = cardW * (7 / 5);
  const cx = w / 2 - cardW / 2;
  const cy = Math.max(24, h / 2 - cardH / 2 - 18);
  const mid = (n - 1) / 2;
  return Array.from({ length: n }, (_, i) => ({
    left: cx + (i - mid) * 6,
    top: cy - (i - mid) * 8,
    width: cardW,
    height: cardH,
  }));
}

export function PackReveal({
  profiles,
  self,
  viewerPlan,
  mode = "deal",
  onSettle,
  onComplete,
}: {
  profiles: MatchProfile[];
  self: MatchProfile;
  viewerPlan: ProfileTier;
  mode?: "deal" | "refresh";
  onSettle?: () => void;
  onComplete: () => void;
}) {
  const t = useT();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const done = useRef(false);
  const settled = useRef(false);
  const timers = useRef<number[]>([]);
  const rafs = useRef<number[]>([]);
  const refresh = mode === "refresh";
  const four = useMemo(() => profiles.slice(0, 4), [profiles]);
  const scoreKey = four.map((p) => p.id).join("|");
  const scores = useMemo(
    () => four.map((p) => pairAlignmentScore(self, p)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scoreKey, self]
  );

  const [phase, setPhase] = useState<Phase>("dark");
  const [gridSeats, setGridSeats] = useState<Seat[]>([]);
  const [stack, setStack] = useState<Seat[]>(() => stackSeats(null, four.length));
  const [shown, setShown] = useState<number[]>(() => scores.map(() => 0));
  /** How many cards have flown to their grid seat (0…4). */
  const [dealt, setDealt] = useState(0);
  const [reduced] = useState(prefersReducedMotion);
  const [signPlay, setSignPlay] = useState(true);

  const clearClocks = () => {
    for (const id of timers.current) window.clearTimeout(id);
    for (const id of rafs.current) window.cancelAnimationFrame(id);
    timers.current = [];
    rafs.current = [];
  };

  const stageOf = () => rootRef.current?.parentElement ?? null;

  const refreshLayout = () => {
    const stage = stageOf();
    setStack(stackSeats(stage, four.length));
    const next = measureSeats(stage);
    if (next.length) setGridSeats(next);
  };

  const finish = () => {
    if (done.current) return;
    done.current = true;
    clearClocks();
    onComplete();
  };

  const goLanded = () => {
    if (settled.current) return;
    settled.current = true;
    clearClocks();
    refreshLayout();
    setDealt(four.length);
    setShown(scores);
    setPhase("landed");
    onSettle?.();
    timers.current.push(
      window.setTimeout(() => {
        setPhase("exit");
        timers.current.push(window.setTimeout(finish, reduced ? 200 : 400));
      }, reduced ? 240 : 850)
    );
  };

  const goExit = () => {
    if (done.current) return;
    if (!settled.current) {
      settled.current = true;
      refreshLayout();
      setDealt(four.length);
      setShown(scores);
      onSettle?.();
    }
    clearClocks();
    setPhase("exit");
    timers.current.push(window.setTimeout(finish, reduced ? 160 : 360));
  };

  const onSkip = () => {
    if (phase === "exit") {
      finish();
      return;
    }
    if (phase === "landed") {
      goExit();
      return;
    }
    setSignPlay(false);
    goLanded();
  };

  const animateScore = (index: number, target: number) => {
    const start = performance.now();
    const tick = (now: number) => {
      if (settled.current || done.current) return;
      const p = Math.min(1, (now - start) / SCORE_MS);
      setShown((prev) => {
        const next = prev.slice();
        next[index] = Math.round(target * p);
        return next;
      });
      if (p < 1) rafs.current.push(window.requestAnimationFrame(tick));
      else {
        setShown((prev) => {
          const next = prev.slice();
          next[index] = target;
          return next;
        });
      }
    };
    rafs.current.push(window.requestAnimationFrame(tick));
  };

  useEffect(() => {
    done.current = false;
    settled.current = false;
    setPhase("dark");
    setSignPlay(true);
    setDealt(0);
    setShown(scores.map(() => 0));
    refreshLayout();
    rafs.current.push(
      window.requestAnimationFrame(() => {
        refreshLayout();
      })
    );

    const signHold = reduced ? SIGN_REDUCED_MS : SIGN_MS;
    const n = four.length;

    if (reduced) {
      timers.current.push(
        window.setTimeout(() => {
          setSignPlay(false);
          setDealt(n);
          setShown(scores);
          goLanded();
        }, signHold)
      );
      return () => clearClocks();
    }

    timers.current.push(
      window.setTimeout(() => {
        setSignPlay(false);
        setPhase("stack");
      }, signHold)
    );

    // Enter deal phase still stacked, then fly one card at a time.
    const dealStart = signHold + 480;
    timers.current.push(
      window.setTimeout(() => {
        refreshLayout();
        setPhase("deal");
        setDealt(0);
      }, dealStart)
    );

    for (let i = 0; i < n; i += 1) {
      const at = dealStart + 80 + i * DEAL_STAGGER_MS;
      timers.current.push(
        window.setTimeout(() => {
          if (settled.current || done.current) return;
          refreshLayout();
          setDealt(i + 1);
          animateScore(i, scores[i] ?? 0);
        }, at)
      );
    }

    timers.current.push(
      window.setTimeout(goLanded, dealStart + 80 + n * DEAL_STAGGER_MS + 700)
    );

    return () => clearClocks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, reduced, scoreKey]);

  useEffect(() => {
    const onResize = () => refreshLayout();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [four.length]);

  const showSign = phase === "dark" && signPlay;

  return (
    <div
      ref={rootRef}
      className={[
        "match-pack",
        refresh ? "match-pack--refresh" : "",
        `is-${phase}`,
        reduced ? "is-reduced" : "",
        showSign ? "is-signing" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      data-viewer-plan={viewerPlan}
      data-dealt={dealt}
      role="dialog"
      aria-modal="true"
      aria-labelledby="match-pack-title"
      onClick={onSkip}
      onKeyDown={(e) => {
        if (e.key === "Escape" || e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSkip();
        }
      }}
      tabIndex={0}
    >
      <div className="match-pack__veil" aria-hidden />
      {showSign ? (
        <div className="match-pack__sign" aria-hidden>
          <InfinityHearts play className="match-pack__sign-mark" />
        </div>
      ) : null}
      <ul className="match-pack__fan" aria-hidden>
        {four.map((p, i) => {
          const cardTier = tierOf(p);
          const atSeat =
            phase === "landed" ||
            phase === "exit" ||
            (phase === "deal" && i < dealt);
          const seat =
            (atSeat && gridSeats[i]) || stack[i] || stackSeats(null, four.length)[i];
          const style = {
            "--i": i,
            left: `${seat.left}px`,
            top: `${seat.top}px`,
            width: `${seat.width}px`,
            height: `${seat.height}px`,
            zIndex: 20 + i,
            transitionDelay: atSeat ? "0ms" : undefined,
          } as CSSProperties;
          return (
            <li
              key={p.id}
              className={`match-pack__card${atSeat ? " is-seated" : ""}`}
              style={style}
              data-slot={i}
              data-plan={cardTier}
              data-score-band={scoreBand(scores[i] ?? 0) ?? undefined}
            >
              <span className="match-pack__card-foil" aria-hidden />
              <span className="match-pack__card-photo">
                <span className="match-pack__card-mono" aria-hidden>
                  {p.firstName.charAt(0)}
                </span>
                {p.photoUrl ? (
                  <img
                    className="match-pack__card-img"
                    src={p.photoUrl}
                    alt=""
                    decoding="async"
                    draggable={false}
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                ) : null}
              </span>
              <span className="match-pack__card-score">
                {shown[i] ?? 0}
                <em>/100</em>
              </span>
            </li>
          );
        })}
      </ul>
      <div className="match-pack__copy">
        <p id="match-pack-title" className="match-pack__title">
          {refresh ? t("match.newSelection") : t("match.packTitle")}
        </p>
        <p className="match-pack__hint">{t("match.packSkip")}</p>
      </div>
    </div>
  );
}
